/**
 * Iter 31.160 — Addresses registry tile.
 *
 * Operator-managed places. Six expects <20 total (current + previous
 * residences + family + holiday rentals). Foundation for the 60k photo
 * bulk-import system: every photo will eventually link to an address_id
 * so the same kitchen across different addresses doesn't conflate.
 *
 * Lives on /admin. Below the Keys & Manifest tile.
 *
 * Workflow:
 *   - "Add address" opens an inline form.
 *   - Each row shows label · city · ownership badge · is-current pill · audit count.
 *   - Inline edit-in-place (toggle to form mode).
 *   - Archive button (soft-delete) with a confirm.
 *   - Toggle "include archived" to surface historical rows.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, MapPin, Edit3, Archive, X, CheckCircle2, AlertTriangle, Search } from "lucide-react";


const OWNERSHIPS = ["self", "family", "other", "unknown"];

const EMPTY_DRAFT = {
    label: "",
    street_address: "",
    city: "",
    region: "",
    postal_code: "",
    country: "",
    gps_centroid: null,
    gps_radius_m: 50,
    date_range_start: "",
    date_range_end: "",
    ownership: "self",
    is_current: false,
    notes: "",
};


function OwnershipBadge({ ownership }) {
    const tones = {
        self: "border-stamp-red text-stamp-red",
        family: "border-amber-700 text-amber-700",
        other: "border-ink/40 text-ink-soft",
        unknown: "border-ink/30 text-ink-faint",
    };
    return (
        <span
            className={`inline-block px-1.5 py-0.5 border ${tones[ownership] || tones.unknown} text-[9px] font-heading uppercase tracking-[0.18em]`}
            data-testid={`address-ownership-${ownership}`}
        >
            {ownership}
        </span>
    );
}


function AddressForm({ initial, onSave, onCancel, busy }) {
    const [draft, setDraft] = useState({ ...EMPTY_DRAFT, ...(initial || {}) });
    const [lookupQuery, setLookupQuery] = useState("");
    const [lookupBusy, setLookupBusy] = useState(false);
    const [lookupErr, setLookupErr] = useState("");
    const [lookupChoices, setLookupChoices] = useState([]);
    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const setGps = (k, v) => {
        const lat = k === "lat" ? Number(v) : Number(draft.gps_centroid?.lat ?? "");
        const lng = k === "lng" ? Number(v) : Number(draft.gps_centroid?.lng ?? "");
        if (!Number.isFinite(lat) && !Number.isFinite(lng)) {
            setDraft((d) => ({ ...d, gps_centroid: null }));
            return;
        }
        setDraft((d) => ({ ...d, gps_centroid: { lat: lat || 0, lng: lng || 0 } }));
    };
    const submit = (e) => {
        e.preventDefault();
        const payload = { ...draft };
        // Empty strings are not valid ISO dates — drop them so the
        // server doesn't 422 on optional fields.
        for (const k of ["street_address", "city", "region", "postal_code", "country", "notes", "date_range_start", "date_range_end"]) {
            if (payload[k] === "") delete payload[k];
        }
        if (payload.gps_centroid && (!Number.isFinite(payload.gps_centroid.lat) || !Number.isFinite(payload.gps_centroid.lng))) {
            delete payload.gps_centroid;
        }
        onSave(payload);
    };

    // Iter 31.161 — Nominatim lookup. Fires /addresses/geocode and surfaces
    // up to 5 candidates; one tap fills the form. Server respects
    // Nominatim's 1 req/sec policy so don't worry about hammering.
    const lookup = async () => {
        if (!lookupQuery.trim()) return;
        setLookupBusy(true); setLookupErr(""); setLookupChoices([]);
        try {
            const { data } = await api.post("/addresses/geocode", { query: lookupQuery });
            const list = data.candidates || [];
            if (list.length === 0) {
                setLookupErr("No matches.");
            } else if (list.length === 1) {
                applyCandidate(list[0]);
            } else {
                setLookupChoices(list);
            }
        } catch (e) {
            setLookupErr(e?.response?.data?.detail || e.message || "lookup failed");
        } finally {
            setLookupBusy(false);
        }
    };
    const applyCandidate = (c) => {
        setDraft((d) => ({
            ...d,
            // Only overwrite label if it's currently blank — Six may have
            // typed "Westfield" already and the candidate's label_suggestion
            // is just the road name.
            label: d.label?.trim() ? d.label : (c.label_suggestion || d.label),
            street_address: c.street_address || d.street_address || "",
            city: c.city || d.city || "",
            region: c.region || d.region || "",
            postal_code: c.postal_code || d.postal_code || "",
            country: c.country || d.country || "",
            gps_centroid: c.gps_centroid || d.gps_centroid,
            gps_radius_m: c.gps_radius_m || d.gps_radius_m || 50,
        }));
        setLookupChoices([]);
    };
    return (
        <form
            onSubmit={submit}
            className="border border-ink/25 p-3 mt-2 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-paper/40"
            data-testid="address-form"
        >
            {/* Iter 31.161 — Nominatim lookup row. Paste a place name or
                Google Maps URL, hit LOOKUP, pre-fills the rest. */}
            <div className="sm:col-span-2 border-b border-ink/15 pb-2" data-testid="address-form-lookup-block">
                <div className="flex gap-1 items-center">
                    <Search className="w-3.5 h-3.5 text-ink-soft shrink-0" />
                    <input
                        type="text"
                        placeholder="Lookup · paste a name or Google Maps URL"
                        value={lookupQuery}
                        onChange={(e) => setLookupQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
                        className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent flex-1 min-w-0"
                        data-testid="address-form-lookup-input"
                    />
                    <button
                        type="button"
                        onClick={lookup}
                        disabled={lookupBusy || !lookupQuery.trim()}
                        className="font-heading uppercase tracking-[0.16em] text-[10px] px-2 py-1 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-50 shrink-0"
                        data-testid="address-form-lookup-btn"
                    >
                        {lookupBusy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "lookup"}
                    </button>
                </div>
                {lookupErr && (
                    <div className="mt-1 text-[10px] text-stamp-red font-body" data-testid="address-form-lookup-err">{lookupErr}</div>
                )}
                {lookupChoices.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="address-form-lookup-choices">
                        <div className="label-typed text-[9px] mb-1">{lookupChoices.length} candidates · tap one to fill the form</div>
                        {lookupChoices.map((c, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => applyCandidate(c)}
                                className="block w-full text-left border border-ink/20 hover:border-stamp-red px-2 py-1 text-[11px] font-body text-ink-soft hover:text-stamp-red"
                                data-testid={`address-form-lookup-candidate-${i}`}
                            >
                                <div className="font-heading uppercase tracking-[0.14em] text-[10px] text-ink">{c.label_suggestion || "(unnamed)"}</div>
                                <div className="text-[10px]">{c.display_name}</div>
                            </button>
                        ))}
                    </div>
                )}
                <div className="mt-1 text-[9px] text-ink-faint italic">via OSM Nominatim · 1 req/sec global throttle · no API key</div>
            </div>
            <input
                type="text" placeholder="Label · e.g. Westfield"
                value={draft.label} onChange={(e) => set("label", e.target.value)}
                required
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
                data-testid="address-form-label"
            />
            <select
                value={draft.ownership} onChange={(e) => set("ownership", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
                data-testid="address-form-ownership"
            >
                {OWNERSHIPS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input
                type="text" placeholder="Street address"
                value={draft.street_address || ""} onChange={(e) => set("street_address", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent sm:col-span-2"
            />
            <input
                type="text" placeholder="City" value={draft.city || ""}
                onChange={(e) => set("city", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
            />
            <input
                type="text" placeholder="Region / county" value={draft.region || ""}
                onChange={(e) => set("region", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
            />
            <input
                type="text" placeholder="Postcode" value={draft.postal_code || ""}
                onChange={(e) => set("postal_code", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
            />
            <input
                type="text" placeholder="Country" value={draft.country || ""}
                onChange={(e) => set("country", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent"
            />
            <input
                type="number" step="0.0001" placeholder="GPS lat"
                value={draft.gps_centroid?.lat ?? ""} onChange={(e) => setGps("lat", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-mono bg-transparent"
            />
            <input
                type="number" step="0.0001" placeholder="GPS lng"
                value={draft.gps_centroid?.lng ?? ""} onChange={(e) => setGps("lng", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-mono bg-transparent"
            />
            <input
                type="text" placeholder="Date range start (YYYY-MM-DD)"
                value={draft.date_range_start || ""} onChange={(e) => set("date_range_start", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-mono bg-transparent"
            />
            <input
                type="text" placeholder="Date range end · blank = still active"
                value={draft.date_range_end || ""} onChange={(e) => set("date_range_end", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-mono bg-transparent"
            />
            <label className="flex items-center gap-2 text-xs sm:col-span-2">
                <input type="checkbox" checked={!!draft.is_current} onChange={(e) => set("is_current", e.target.checked)} data-testid="address-form-is-current" />
                <span className="font-heading uppercase tracking-[0.18em] text-[10px]">Mark as current address</span>
            </label>
            <textarea
                placeholder="Notes"
                value={draft.notes || ""} onChange={(e) => set("notes", e.target.value)}
                className="border border-ink/30 px-2 py-1 text-xs font-body bg-transparent sm:col-span-2 min-h-[3em]"
            />
            <div className="flex gap-2 sm:col-span-2 justify-end">
                <button
                    type="button" onClick={onCancel}
                    className="font-heading uppercase tracking-[0.16em] text-[10px] px-2.5 py-1 border border-ink/40 text-ink-soft hover:text-stamp-red hover:border-stamp-red"
                    data-testid="address-form-cancel"
                >cancel</button>
                <button
                    type="submit" disabled={busy || !draft.label.trim()}
                    className="font-heading uppercase tracking-[0.16em] text-[10px] px-2.5 py-1 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-50"
                    data-testid="address-form-save"
                >{busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "save"}</button>
            </div>
        </form>
    );
}


function AddressRow({ addr, onEdit, onArchive, busy, expandedId }) {
    const isExpanded = expandedId === addr.id;
    return (
        <div className="border-b border-ink/15 py-2 last:border-b-0" data-testid={`address-row-${addr.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <MapPin className="w-3.5 h-3.5 text-ink-soft shrink-0" />
                        <span className="font-heading uppercase tracking-[0.18em] text-[11px] text-ink">{addr.label}</span>
                        <OwnershipBadge ownership={addr.ownership} />
                        {addr.is_current && <span className="inline-block px-1.5 py-0.5 border border-emerald-700 text-emerald-700 text-[9px] font-heading uppercase tracking-[0.18em]">current</span>}
                        {addr.archived && <span className="inline-block px-1.5 py-0.5 border border-ink/30 text-ink-faint text-[9px] font-heading uppercase tracking-[0.18em]">archived</span>}
                    </div>
                    <div className="text-[10px] text-ink-soft font-body pl-5 mt-0.5">
                        {[addr.street_address, addr.city, addr.region, addr.postal_code, addr.country].filter(Boolean).join(" · ") || <span className="italic text-ink-faint">no address details</span>}
                    </div>
                    {addr.gps_centroid && (
                        <div className="text-[10px] text-ink-faint font-mono pl-5">
                            GPS · {addr.gps_centroid.lat.toFixed(4)}, {addr.gps_centroid.lng.toFixed(4)} · r{addr.gps_radius_m}m
                        </div>
                    )}
                </div>
                <div className="flex gap-1 shrink-0">
                    <button
                        type="button" onClick={() => onEdit(addr)}
                        className="font-heading uppercase tracking-[0.16em] text-[10px] px-2 py-0.5 border border-ink/40 text-ink-soft hover:text-stamp-red hover:border-stamp-red"
                        disabled={busy}
                        data-testid={`address-edit-${addr.id}`}
                    >
                        <Edit3 className="w-3 h-3 inline mr-1" />edit
                    </button>
                    {!addr.archived && (
                        <button
                            type="button" onClick={() => onArchive(addr)}
                            className="font-heading uppercase tracking-[0.16em] text-[10px] px-2 py-0.5 border border-stamp-red/60 text-stamp-red/80 hover:bg-stamp-red hover:text-paper"
                            disabled={busy}
                            data-testid={`address-archive-${addr.id}`}
                        >
                            <Archive className="w-3 h-3 inline mr-1" />archive
                        </button>
                    )}
                </div>
            </div>
            {isExpanded && addr.notes && (
                <div className="text-[10px] text-ink-soft font-body pl-5 mt-1 italic">{addr.notes}</div>
            )}
        </div>
    );
}


export default function AddressesPanel() {
    const [rows, setRows] = useState([]);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);   // null | "new" | <addrObject>
    const [lastSaved, setLastSaved] = useState(null);

    const load = async () => {
        try {
            setErr("");
            const { data } = await api.get(`/addresses?include_archived=${includeArchived ? "true" : "false"}`);
            setRows(data.items || []);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

    const save = async (payload) => {
        setBusy(true); setErr("");
        try {
            let saved;
            if (editing === "new") {
                ({ data: saved } = await api.post("/addresses", payload));
            } else {
                ({ data: saved } = await api.patch(`/addresses/${editing.id}`, payload));
            }
            setLastSaved({ label: saved.label, at: saved.updated_at });
            setEditing(null);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    const archive = async (addr) => {
        if (!window.confirm(`Archive "${addr.label}"? Soft-delete only — historical photo links won't dangle, and you can restore via the include-archived toggle + edit form (set archived → false).`)) return;
        setBusy(true);
        try {
            await api.delete(`/addresses/${addr.id}`);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="paper-card border-2 border-ink/30 p-5 mb-6" data-testid="admin-addresses-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-stamp-red" />
                    Addresses
                    <span className="text-ink-faint text-[10px] tracking-normal">· {rows.length} on file</span>
                </h2>
                <div className="flex gap-2 items-center">
                    <label className="flex items-center gap-1 text-[10px] font-heading uppercase tracking-[0.16em] text-ink-soft">
                        <input
                            type="checkbox" checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            data-testid="addresses-include-archived"
                        /> include archived
                    </label>
                    <button
                        type="button" onClick={() => setEditing("new")} disabled={busy || editing}
                        className="font-heading uppercase tracking-[0.16em] text-[10px] px-2.5 py-1 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-50"
                        data-testid="addresses-add"
                    >
                        <Plus className="w-3 h-3 inline mr-1" />add address
                    </button>
                </div>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.160 foundation — operator-managed registry of places. Each photo
                ingested by the bulk-import lane (iter 31.161+) will link to one of these
                addresses so the kitchen at Westfield doesn't conflate with the kitchen at
                the old flat. Pre-seed your current address before kicking off the bulk
                import.
            </div>

            {err && (
                <div className="mb-3 text-[11px] border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /><span>{err}</span>
                </div>
            )}
            {lastSaved && (
                <div className="mb-3 text-[11px] border-l-2 border-emerald-700 pl-3 text-emerald-700 flex items-start gap-1" data-testid="addresses-last-saved">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Saved <code className="font-mono">{lastSaved.label}</code> at {lastSaved.at}.</span>
                </div>
            )}

            {editing === "new" && (
                <AddressForm onSave={save} onCancel={() => setEditing(null)} busy={busy} />
            )}

            {loading ? (
                <div className="font-body text-[11px] text-ink-faint italic">Loading addresses…</div>
            ) : rows.length === 0 ? (
                <div className="font-body text-[11px] text-ink-faint italic" data-testid="addresses-empty">
                    No addresses on file yet. Add your current address to seed the registry.
                </div>
            ) : (
                <div>
                    {rows.map((addr) => (
                        <React.Fragment key={addr.id}>
                            <AddressRow addr={addr} onEdit={setEditing} onArchive={archive} busy={busy} expandedId={null} />
                            {editing && typeof editing === "object" && editing.id === addr.id && (
                                <AddressForm initial={addr} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}
